import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { push } from 'redux-first-history';

import { safelyEncodeBase64Url } from '@dev/ee-tools';
import { SimpleLandingCheckout } from '@dev/ee-api/ee-api-models';

import { CheckoutsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { DeleteCheckout } from '@/store/checkouts/actions';
import { DropdownOptions } from '@/store/checkouts/types';
import { ShowDialog, UpdateDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';

import { CHECKOUT_DELETE_CONFIRMATION_TEXT } from '@/components/Checkouts/CheckoutsInterface';
import { useCheckoutParamsFromUrl } from '@/components/Checkouts/hooks/useRequestParams';
import { CheckoutLinkModalBody } from '../../components/CheckoutLinkModalBody';
import { CheckoutDeleteModalBody } from '../../components/CheckoutDeleteModalBody';


export const useDropdownActions = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const urlParams = useCheckoutParamsFromUrl();

    const handleDeleteCheckout = (name: string) => {
        const changeDeleteConfirmationText = (text): void => {
            setDeleteConfirmationText(text);
            dispatch(UpdateDialog({ isDisabled: text !== CHECKOUT_DELETE_CONFIRMATION_TEXT }));
        };

        const dialogParams: ShowDialogParams = {
            title: t('deletecheckout', { count: 1 }),
            destructiveLabel: t('delete'),
            onClickDestructive: (): void => {
                dispatch(DeleteCheckout([name], urlParams));
                setDeleteConfirmationText('');
            },
            cancelLabel: t('cancel'),
            onClickCancel: () => setDeleteConfirmationText(''),
            body: <CheckoutDeleteModalBody
                checkedCheckouts={[name]}
                deleteConfirmationText={deleteConfirmationText}
                setDeleteConfirmationText={setDeleteConfirmationText}
                changeDeleteConfirmationText={changeDeleteConfirmationText}
            />,
            isDisabled: deleteConfirmationText !== CHECKOUT_DELETE_CONFIRMATION_TEXT
        };

        dispatch(ShowDialog(dialogParams));
    };

    const handleLinkCheckout = (checkout: SimpleLandingCheckout) => {
        const dialogParams: ShowDialogParams = {
            title: t('checkoutlink'),
            body: <CheckoutLinkModalBody checkout={checkout}/>,
            width: '820px',
            cancelLabel: t('close')
        };

        dispatch(ShowDialog(dialogParams));
    };

    const commonOptions = {
        [DropdownOptions.CheckoutLink]: (checkout: SimpleLandingCheckout) => handleLinkCheckout(checkout),
        [DropdownOptions.EditCheckout]: (checkout: SimpleLandingCheckout) =>                             
            {dispatch(push(CheckoutsPaths.CheckoutCreator.replace(pathsParams.nameOptional, safelyEncodeBase64Url(checkout.Name))))},
        [DropdownOptions.Delete]: (checkout: SimpleLandingCheckout) => handleDeleteCheckout(checkout.Name),
    };

    const getDropdownActions = (
        option: { label?: string; value: DropdownOptions }, 
        checkout: SimpleLandingCheckout
    ): void => commonOptions[option.value](checkout);

    return { 
        getDropdownActions,
        showCheckoutLinkModal: handleLinkCheckout
    }
};
